import { useEffect, type ReactNode } from 'react'

/**
 * Confirmación de una acción que cambia QUIÉN le habla al cliente.
 *
 * «Devolver al agente» (DEF-023) no se deshace con otro clic: el próximo
 * mensaje del cliente ya lo contesta el agente IA, y el agente le avisa que
 * retoma. Una pregunta de un segundo es más barata que un cliente que recibe
 * al bot cuando esperaba a una persona.
 *
 * «Marcar resuelta» con un mensaje del asesor sin contestar (DEF-004) es el
 * mismo problema por el otro lado: el 01-oct-2026 un asesor saludó a las
 * 20:18:27Z y resolvió 9 s después; el «Hola» del cliente abrió una sesión
 * nueva con el menú del bot, y el cliente leyó dos identidades seguidas.
 *
 * Es genérica a propósito: cualquier acción de la bandeja que cambie quién
 * atiende pasa por acá, y dos copias del mismo diálogo se comportan distinto
 * en cuanto una cambie. Escape y el fondo cancelan; el botón de confirmar es
 * el único que ejecuta.
 */
export default function Confirmacion({
  titulo, children, confirmar, onConfirmar, onCancelar, tono = 'brand', cancelar = 'Cancelar',
}: {
  titulo: string
  children: ReactNode
  /** Etiqueta del botón que ejecuta. Dice la acción, no «Aceptar». */
  confirmar: string
  onConfirmar: () => void
  onCancelar: () => void
  /** `warn` cuando confirmar tiene una consecuencia que conviene evitar. */
  tono?: 'brand' | 'warn'
  /** Etiqueta del botón que no hace nada. «Ahora no» cuando es un aviso. */
  cancelar?: string
}) {
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancelar() }
    document.addEventListener('keydown', alTeclear)
    return () => document.removeEventListener('keydown', alTeclear)
  }, [onCancelar])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmacion-titulo"
      onClick={onCancelar}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex w-[min(460px,100%)] flex-col gap-2.5 rounded-panel bg-white p-6 shadow-panel"
      >
        <h2 id="confirmacion-titulo" className="text-card font-extrabold text-ink">{titulo}</h2>
        <div className="text-body leading-relaxed text-ink-muted">{children}</div>
        <div className="mt-3 flex justify-end gap-2.5">
          <button onClick={onCancelar} className="btn-ghost">{cancelar}</button>
          <button
            onClick={onConfirmar}
            autoFocus
            className={`rounded-btn px-[15px] py-2 text-body font-bold text-white transition-colors ${
              tono === 'warn' ? 'bg-warn hover:opacity-90' : 'bg-brand hover:bg-brand-hover'
            }`}
          >
            {confirmar}
          </button>
        </div>
      </div>
    </div>
  )
}
